import { memo, type HTMLAttributes } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Square, Color, PieceType } from '@fog-chess/core';
import { Piece } from './Piece';
import { FogOverlay } from './FogOverlay';
import { DraggablePiece } from './DraggablePiece';
import { cn } from '@/lib/utils';

/** Visual-only props for SquareVisual (memoized) */
export interface SquareVisualProps extends HTMLAttributes<HTMLDivElement> {
  /** Square coordinate (e.g., 'e4') */
  square: Square;
  /** Whether this is a light square */
  isLight: boolean;
  /** Piece occupying this square (if any) */
  piece: { type: PieceType; color: Color } | null;
  /** Fog state: 'hidden' = opponent piece hidden, 'visible' = own piece, 'none' = empty */
  fogState: 'hidden' | 'visible' | 'none';
  /** Whether this square is selected */
  isSelected: boolean;
  /** Whether this square is the source of the last move */
  isLastMoveFrom: boolean;
  /** Whether this square is the target of the last move */
  isLastMoveTo: boolean;
  /** Whether a King drop is forbidden here (setup: front rank) */
  isKingShieldForbidden: boolean;
  /** Whether a high-value piece drop would trigger warning (setup: Q/R on front rank) */
  isFrontRankWarning: boolean;
  /** Whether a piece is currently being dragged over this square */
  isDropActive: boolean;
  /** Whether this square is a valid drop target (for visual feedback) */
  isValidDropTarget: boolean;
  /** Current drag preview piece (for ghost rendering) */
  dragPreviewPiece: { type: PieceType; color: Color } | null;
}

/**
 * SquareVisual — Pure memoized visual representation of a board square.
 *
 * Contains ZERO hooks, ZERO @dnd-kit subscriptions. Receives only primitive
 * props and renders the square background, piece, fog overlay, and indicators.
 * Wrapped in React.memo with custom comparator for maximum render isolation.
 */
export const SquareVisual = memo(function SquareVisual({
  square,
  isLight,
  piece,
  fogState,
  isSelected,
  isLastMoveFrom,
  isLastMoveTo,
  isKingShieldForbidden,
  isFrontRankWarning,
  isDropActive,
  isValidDropTarget,
  dragPreviewPiece,
  className,
  style,
  children,
  ...rest
}: SquareVisualProps) {
  // Base square colors from theme
  const baseBg = isLight
    ? 'bg-[hsl(var(--board-light))]'
    : 'bg-[hsl(var(--board-dark))]';

  // Selection ring (cyan)
  const selectionRing = isSelected
    ? 'ring-2 ring-[hsl(var(--primary))] ring-inset'
    : '';

  // Last move highlights (amber)
  const lastMoveHighlight = (isLastMoveFrom || isLastMoveTo)
    ? 'bg-[hsl(var(--accent)/0.35)] ring-1 ring-[hsl(var(--accent))] ring-inset'
    : '';

  // Drop target highlight
  const dropHighlight = isDropActive && isValidDropTarget
    ? 'bg-[hsl(var(--primary)/0.2)] ring-2 ring-[hsl(var(--primary))] ring-inset'
    : '';

  // King Shield forbidden overlay (red X pattern)
  const kingShieldForbidden = isKingShieldForbidden
    ? 'relative after:content-[""] after:absolute after:inset-0 after:bg-[repeating-linear-gradient(45deg,hsl(var(--destructive)/0.15)_0_6px,hsl(var(--destructive)/0.25)_6px_12px)] after:pointer-events-none'
    : '';

  // Front rank warning border (amber)
  const frontRankWarning = isFrontRankWarning
    ? 'ring-2 ring-[hsl(var(--warning))] ring-inset'
    : '';

  // Drag preview ghost piece
  const showDragPreview = dragPreviewPiece && !piece && isDropActive && isValidDropTarget;

  return (
    <div
      className={cn(
        'relative size-full flex items-center justify-center select-none',
        baseBg,
        selectionRing,
        lastMoveHighlight,
        dropHighlight,
        kingShieldForbidden,
        frontRankWarning,
        className,
      )}
      style={style}
      data-square={square}
      data-light={isLight.toString()}
      {...rest}
    >
      {/* Base square background */}
      <div className="absolute inset-0" />

      {/* Piece or drag preview */}
      {(piece || showDragPreview) && (
        <Piece
          type={piece ? piece.type : dragPreviewPiece!.type}
          color={piece ? piece.color : dragPreviewPiece!.color}
          size={56}
          isDragging={false}
          className={cn(
            'transition-transform duration-75 ease-out',
            showDragPreview && 'opacity-60 scale-95',
          )}
        />
      )}

      {/* Fog of War overlay (opponent hidden pieces) */}
      {fogState === 'hidden' && <FogOverlay isLight={isLight} opacity={0.88} />}

      {/* King Shield forbidden indicator (X icon) */}
      {isKingShieldForbidden && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          aria-label="King cannot be placed on front rank"
        >
          <svg
            className="size-10 text-[hsl(var(--destructive))/0.9]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="4" y1="4" x2="20" y2="20" />
            <line x1="20" y1="4" x2="4" y2="20" />
          </svg>
        </div>
      )}

      {/* Front rank warning banner indicator (small warning icon) */}
      {isFrontRankWarning && (
        <div
          className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center"
          aria-label="High-value piece on front rank: blind sniping risk"
        >
          <svg
            className="size-4 text-[hsl(var(--warning))]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
      )}

      {children}
    </div>
  );
}, (prev, next) => {
  // Custom memoization: only these props trigger re-render
  return (
    prev.square === next.square &&
    prev.isLight === next.isLight &&
    prev.piece?.type === next.piece?.type &&
    prev.piece?.color === next.piece?.color &&
    prev.fogState === next.fogState &&
    prev.isSelected === next.isSelected &&
    prev.isLastMoveFrom === next.isLastMoveFrom &&
    prev.isLastMoveTo === next.isLastMoveTo &&
    prev.isKingShieldForbidden === next.isKingShieldForbidden &&
    prev.isFrontRankWarning === next.isFrontRankWarning &&
    prev.isDropActive === next.isDropActive &&
    prev.isValidDropTarget === next.isValidDropTarget &&
    prev.dragPreviewPiece?.type === next.dragPreviewPiece?.type &&
    prev.dragPreviewPiece?.color === next.dragPreviewPiece?.color
  );
});

SquareVisual.displayName = 'SquareVisual';

/** Container props for DroppableSquare */
export interface DroppableSquareProps {
  square: Square;
  isLight: boolean;
  piece: { type: PieceType; color: Color } | null;
  fogState: 'hidden' | 'visible' | 'none';
  isSelected: boolean;
  isLastMoveFrom: boolean;
  isLastMoveTo: boolean;
  isKingShieldForbidden: boolean;
  isFrontRankWarning: boolean;
  isSetupPhase: boolean;
  isGameplayPhase: boolean;
  isMyTurn: boolean;
  viewerColor: Color | null;
  occupiedSquares: ReadonlySet<Square>;
  activeDragPiece: { type: PieceType; color: Color } | null;
  onSquareClick: (square: Square) => void;
}

/**
 * DroppableSquare — Container component that subscribes to @dnd-kit droppable context.
 *
 * This component uses `useDroppable` and passes primitive values to `SquareVisual`,
 * ensuring the visual component remains purely memoized and never re-renders
 * due to dnd-kit context changes.
 */
export function DroppableSquare({
  square,
  isLight,
  piece,
  fogState,
  isSelected,
  isLastMoveFrom,
  isLastMoveTo,
  isKingShieldForbidden,
  isFrontRankWarning,
  isSetupPhase,
  isGameplayPhase,
  isMyTurn,
  viewerColor,
  occupiedSquares = new Set(), // Fallback if undefined
  activeDragPiece = null,      // Fallback if undefined
  onSquareClick,
}: DroppableSquareProps) {
  const droppableId = `square-${square}`;

  // Determine if this square's piece is draggable using both setup and gameplay phases
  const isDraggable = !!(
    piece &&
    (isSetupPhase || (isGameplayPhase && isMyTurn)) &&
    piece.color === viewerColor
  );

  // Only enable droppable during setup phase for empty squares in setup zone
  // During gameplay, drops are validated server-side; we still allow drop for UX
  const isDroppableEnabled =
    isSetupPhase &&
    !occupiedSquares.has(square) &&
    piece === null; // Only empty squares accept drops in setup

  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: droppableId,
    disabled: !isDroppableEnabled,
  });

  // Determine if this square is a valid drop target for the currently dragged piece
  const isValidDropTarget = isOver && isDroppableEnabled && activeDragPiece !== null;
  const isDropActive = isOver;

  return (
    <div
      ref={setNodeRef}
      onClick={() => onSquareClick(square)}
      className="relative size-full cursor-pointer touch-none"
      data-square={square}
    >
      <SquareVisual
        square={square}
        isLight={isLight}
        piece={piece}
        fogState={fogState}
        isSelected={isSelected}
        isLastMoveFrom={isLastMoveFrom}
        isLastMoveTo={isLastMoveTo}
        isKingShieldForbidden={isKingShieldForbidden}
        isFrontRankWarning={isFrontRankWarning}
        isDropActive={isDropActive}
        isValidDropTarget={isValidDropTarget}
        dragPreviewPiece={isValidDropTarget ? activeDragPiece : null}
      />
      {isDraggable && piece && (
        <DraggablePiece
          square={square}
          type={piece.type}
          color={piece.color}
          isSetupPhase={isSetupPhase}
          isGameplayPhase={!isSetupPhase}
          isMyTurn={isMyTurn}
          viewerColor={viewerColor}
          size={56 * 0.85}
        />
      )}
      {fogState === 'hidden' && <FogOverlay isLight={isLight} opacity={0.88} />}
    </div>
  );
}